import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Camera } from "lucide-react";
import { fetchHero, resolveUrl } from "../lib/api";
import { useSiteTexts } from "../lib/siteTexts";

const FALLBACK_HERO = "https://customer-assets-jai6qajn.emergentagent.net/job_saloni-bday-surprise/artifacts/6g92nq5q_IMG_9912.webp";

const DEFAULT_TAGLINES = [
  "Aging like fine wine, or maybe just whining fine?",
  "Welcome to your digital surprise, Bob.",
  "One reheated coffee closer to legend.",
];

const SUB_HEADLINES = [
  "Aging like fine wine, or maybe just whining fine?",
  "Welcome to your digital surprise, Bob.",
  "One reheated coffee closer to legend.",
  "Anti-mithai activist. Softy loyalist. It's complicated.",
  "Via's boss's boss celebrates today.",
  "Pani puri round two is a personality trait.",
  "Perk's favourite person. Pista's problem child.",
];

export default function Hero({ onExplore }) {
  const [idx, setIdx] = useState(0);
  const [heroes, setHeroes] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const texts = useSiteTexts();

  const taglines = (texts.hero_taglines
    ? texts.hero_taglines.split("|").map((s) => s.trim()).filter(Boolean)
    : SUB_HEADLINES);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % taglines.length), 3200);
    return () => clearInterval(t);
  }, [taglines.length]);

  useEffect(() => {
    fetchHero(4)
      .then((data) => setHeroes(Array.isArray(data) ? data : []))
      .catch(() => setHeroes([]));
  }, []);

  useEffect(() => {
    if (heroes.length < 2) return;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % heroes.length), 6000);
    return () => clearInterval(t);
  }, [heroes.length]);

  const currentHero = heroes[heroIdx];
  const heroSrc = currentHero?.url ? resolveUrl(currentHero.url) : FALLBACK_HERO;
  const heroTitle = currentHero?.title || "Chief Mood Officer";

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden"
    >
      <div className="absolute inset-0 hero-glow" />
      <div className="absolute inset-0 grain" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-8">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.35em] text-[#334E68] font-body">
            <span className="w-8 h-px bg-[#7B9ACC]" />
            A birthday, but make it soft-blue
          </div>
          <h1 className="mt-6 font-display font-black text-[#0B192C] tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
            Happy Birthday, <br />
            <span className="italic font-bold">Saloni.</span>
            <span className="font-hand text-[#7B9ACC] text-4xl sm:text-5xl block mt-3">
              a.k.a. Bob • Onnii
            </span>
          </h1>

          <div className="mt-8 h-8 relative overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.p
                key={idx}
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -24, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="font-body text-lg sm:text-xl text-[#334E68]"
                data-testid={`hero-tagline-${idx}`}
              >
                {taglines[idx]}
              </motion.p>
            </AnimatePresence>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-explore"
              onClick={() => onExplore("memories")}
              className="group relative rounded-full bg-[#0B192C] text-[#F7F9FC] px-7 py-3 font-body text-sm tracking-wide hover:-translate-y-0.5 transition-transform duration-300"
            >
              Walk down memory lane
              <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <button
              data-testid="hero-cta-about"
              onClick={() => onExplore("about")}
              className="rounded-full border border-[#0B192C]/20 px-7 py-3 font-body text-sm text-[#0B192C] hover:bg-white transition-colors duration-300 flex items-center gap-2"
            >
              <Camera className="w-4 h-4" />
              Meet the woman of the hour
            </button>
          </div>
        </div>

        <div className="md:col-span-4 relative">
          <TiltPhoto heroSrc={heroSrc} heroTitle={heroTitle} heroes={heroes} heroIdx={heroIdx} setHeroIdx={setHeroIdx} />
          <div className="absolute -top-6 -left-6 rounded-full bg-white border border-[#EAF0F6] px-4 py-2 shadow-sm">
            <span className="font-hand text-[#7B9ACC] text-xl">it's your day 💙</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// HomepageReel removed per user request.

// 3D cursor-tilt hero photo card with glow that follows the cursor.
function TiltPhoto({ heroSrc, heroTitle, heroes, heroIdx, setHeroIdx }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);

  const springOpts = { stiffness: 220, damping: 22, mass: 0.4 };
  const rotateY = useSpring(useTransform(mx, [0, 1], [10, -10]), springOpts);
  const rotateX = useSpring(useTransform(my, [0, 1], [-10, 10]), springOpts);
  const glowX = useTransform(mx, [0, 1], ["0%", "100%"]);
  const glowY = useTransform(my, [0, 1], ["0%", "100%"]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ perspective: 1200 }}
      className="relative"
      data-testid="hero-tilt-card"
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        transition={{ type: "spring", ...springOpts }}
        className="relative aspect-[4/5] rounded-[2rem] overflow-hidden card-shadow bg-[#0B192C]"
      >
        {/* Photo */}
        <AnimatePresence mode="wait">
          <motion.img
            key={heroSrc}
            src={heroSrc}
            alt={heroTitle}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
        </AnimatePresence>

        {/* Cursor-tracking radial glow */}
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none mix-blend-screen"
          style={{
            background: useTransform(
              [glowX, glowY],
              ([x, y]) =>
                `radial-gradient(220px circle at ${x} ${y}, rgba(255,255,255,0.28), transparent 55%)`
            ),
          }}
        />
        {/* Subtle sheen on tilt */}
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0.09) 100%)",
          }}
        />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#0B192C]/85 via-[#0B192C]/40 to-transparent" />

        {heroes.length > 1 && (
          <div className="absolute top-4 right-4 z-20 flex gap-1.5" data-testid="hero-dots" style={{ transform: "translateZ(60px)" }}>
            {heroes.map((_, i) => (
              <button
                key={i}
                onClick={() => setHeroIdx(i)}
                aria-label={`Hero photo ${i + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${
                  i === heroIdx ? "bg-white w-6" : "bg-white/50 w-1.5 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        )}

        <div className="relative z-10 h-full flex flex-col justify-end p-6" style={{ transform: "translateZ(40px)" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={heroTitle}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="rounded-2xl bg-white/85 backdrop-blur-md p-4 border border-white/60"
            >
              <p className="font-hand text-[#0B192C] text-2xl leading-tight">
                "The queen has entered the chat."
              </p>
              <p className="mt-2 font-body text-xs text-[#334E68] tracking-widest uppercase">
                {heroTitle} • estd. today, celebrated forever
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Outer soft glow shadow that pulses gently */}
      <motion.div
        aria-hidden
        className="absolute -inset-4 rounded-[2.5rem] pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 50%, rgba(167,199,231,0.30), transparent 70%)",
          filter: "blur(20px)",
          zIndex: -1,
        }}
        animate={{ opacity: [0.55, 0.85, 0.55] }}
        transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
      />
    </motion.div>
  );
}
